import React, { useState, useEffect } from 'react';
import { validateMandatoryFields, MANDATORY_FIELD_LABELS } from '../utils/formValidation';
import { RadarProfilePanel } from '../components/charts/RadarProfilePanel';
import { computeRadarFromFormData } from '../utils/radarCompute';

interface ProfileReviewPageProps {
  formData: any;
  onEdit: (sectionIndex: number) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

interface ReviewField {
  label: string;
  value: any;
  critical?: boolean;
  highlight?: boolean;
}

interface ReviewSection {
  name: string;
  fields: (data: any) => ReviewField[];
}

const ProfileReviewPage: React.FC<ProfileReviewPageProps> = ({
  formData,
  onEdit,
  onConfirm,
  onCancel
}) => {
  const [confirmed, setConfirmed] = useState(false);
  const [validationResult, setValidationResult] = useState(validateMandatoryFields(formData));
  const [showValidationError, setShowValidationError] = useState(false);

  // Re-validate when formData changes
  useEffect(() => {
    setValidationResult(validateMandatoryFields(formData));
  }, [formData]);

  const sections: ReviewSection[] = [
    { name: "Basic Information", fields: getBasicInfoFields },
    { name: "Swing Characteristics", fields: getSwingCharFields },
    { name: "Ball Flight Data", fields: getBallFlightFields },
    { name: "Physical Profile", fields: getPhysicalProfileFields },
    { name: "Equipment History", fields: getEquipmentHistoryFields },
    { name: "Playing Profile", fields: getPlayingProfileFields },
    { name: "Preferences", fields: getPreferencesFields },
    { name: "Goals & Challenges", fields: getGoalsFields }
  ];

  function getBasicInfoFields(data: any): ReviewField[] {
    return [
      { label: 'Handicap', value: data.handicap, critical: true },
      { label: 'Age', value: data.age, critical: true },
      { label: 'Height', value: data.height_inches ? `${data.height_inches}"` : null, critical: true },
      { label: 'Years Playing', value: data.years_playing, critical: true },
      { label: 'Rounds Per Year', value: data.rounds_per_year },
      { label: 'Dominant Hand', value: data.dominant_hand, critical: true }
    ];
  }

  function getSwingCharFields(data: any): ReviewField[] {
    const driver = data.driver_swing_data || {};
    const iron = data.iron_swing_data || {};

    // Handle both nested (driver_swing_data.swing_speed_driver) and flat (swing_speed_driver) formats
    const driverSwingSpeed = driver.swing_speed_driver || data.swing_speed_driver;
    const driverAttackAngle = driver.attack_angle_driver || data.attack_angle_driver;
    const ironSwingSpeed = iron.swing_speed_7iron || data.swing_speed_7iron;
    const ironAttackAngle = iron.attack_angle_irons || data.attack_angle_irons;

    return [
      { label: 'Driver Swing Speed', value: driverSwingSpeed ? `${driverSwingSpeed} mph` : null, critical: true, highlight: true },
      { label: 'Driver Attack Angle', value: driverAttackAngle ? `${driverAttackAngle}°` : null, critical: true },
      { label: '7-Iron Swing Speed', value: ironSwingSpeed ? `${ironSwingSpeed} mph` : null },
      { label: 'Iron Attack Angle', value: ironAttackAngle ? `${ironAttackAngle}°` : null }
    ];
  }

  function getBallFlightFields(data: any): ReviewField[] {
    const driver = data.launch_monitor_driver || {};
    const iron = data.launch_monitor_iron || {};
    const wedge = data.launch_monitor_wedge || {};

    return [
      // Driver
      { label: 'Driver Launch Angle', value: driver.launch_angle_driver ? `${driver.launch_angle_driver}°` : null, critical: true },
      { label: 'Driver Spin Rate', value: driver.spin_rate_driver ? `${driver.spin_rate_driver} rpm` : null, critical: true },
      { label: 'Driver Ball Speed', value: driver.ball_speed_driver ? `${driver.ball_speed_driver} mph` : null, critical: true },
      { label: 'Driver Carry Distance', value: driver.driver_carry_distance ? `${driver.driver_carry_distance} yds` : null },
      { label: 'Driver Face-to-Path', value: driver.face_to_path_driver ? `${driver.face_to_path_driver}°` : null },
      { label: 'Driver Spin Axis', value: driver.spin_axis_driver ? `${driver.spin_axis_driver} rpm` : null },

      // 7-Iron
      { label: '7-Iron Launch Angle', value: iron.launch_angle_7i ? `${iron.launch_angle_7i}°` : null, critical: true },
      { label: '7-Iron Spin Rate', value: iron.spin_rate_7i ? `${iron.spin_rate_7i} rpm` : null, critical: true },
      { label: '7-Iron Ball Speed', value: iron.ball_speed_7i ? `${iron.ball_speed_7i} mph` : null, critical: true },
      { label: '7-Iron Carry Distance', value: iron.iron_carry_distance ? `${iron.iron_carry_distance} yds` : null },
      { label: '7-Iron Face-to-Path', value: iron.face_to_path_7i ? `${iron.face_to_path_7i}°` : null },

      // Wedges
      { label: 'Wedge Launch Angle', value: wedge.launch_angle_wedge ? `${wedge.launch_angle_wedge}°` : null },
      { label: 'Wedge Spin Rate', value: wedge.spin_rate_wedge ? `${wedge.spin_rate_wedge} rpm` : null },
      { label: 'Wedge Carry Distance', value: wedge.wedge_carry_distance ? `${wedge.wedge_carry_distance} yds` : null }
    ];
  }

  function getPhysicalProfileFields(data: any): ReviewField[] {
    const physical = data.physical_profile || {};
    return [
      { label: 'Wrist to Floor', value: physical.wrist_to_floor ? `${physical.wrist_to_floor}"` : null },
      { label: 'Flexibility Rating', value: physical.flexibility_rating ? `${physical.flexibility_rating}/10` : null },
      { label: 'Core Strength', value: physical.core_strength ? `${physical.core_strength}/10` : null },
      { label: 'Weekly Workouts', value: physical.weekly_workouts },
      { label: 'Posture Type', value: physical.posture_type }
    ];
  }

  function getEquipmentHistoryFields(data: any): ReviewField[] {
    const equipment = data.equipment_history || {};
    const driver = equipment.current_clubs_driver || {};
    const irons = equipment.current_clubs_irons || {};
    const wedge = equipment.current_clubs_wedges || {};

    return [
      { label: 'Current Driver Satisfaction', value: equipment.current_driver_satisfaction ? `${equipment.current_driver_satisfaction}/10` : null },
      { label: 'Current Driver', value: driver.brand && driver.model ? `${driver.brand} ${driver.model}` : null },
      { label: 'Driver Shaft', value: driver.shaft_flex && driver.shaft_model ? `${driver.shaft_model} (${driver.shaft_flex})` : null, critical: true },

      { label: 'Current Iron Satisfaction', value: equipment.current_iron_satisfaction ? `${equipment.current_iron_satisfaction}/10` : null },
      { label: 'Current Irons', value: irons.brand && irons.model ? `${irons.brand} ${irons.model}` : null },
      { label: 'Iron Shaft', value: irons.shaft_flex && irons.shaft_model ? `${irons.shaft_model} (${irons.shaft_flex})` : null, critical: true },

      { label: 'Current Wedge Satisfaction', value: equipment.current_wedge_satisfaction ? `${equipment.current_wedge_satisfaction}/10` : null },
      { label: 'Current Wedge', value: wedge.brand && wedge.model ? `${wedge.brand} ${wedge.model}` : null }
    ];
  }

  function getPlayingProfileFields(data: any): ReviewField[] {
    const playing = data.playing_profile || {};
    return [
      { label: 'Typical Course Type', value: playing.typical_course_type },
      { label: 'Course Difficulty', value: playing.course_difficulty },
      { label: 'Weather Conditions', value: playing.weather_conditions },
      { label: 'Scoring Consistency', value: playing.scoring_consistency }
    ];
  }

  function getPreferencesFields(data: any): ReviewField[] {
    const prefs = data.preferences || {};
    return [
      { label: 'Preferred Brands', value: prefs.brand_preferences },
      { label: 'Aesthetic Preferences', value: prefs.aesthetic_preferences },
      { label: 'Feel Preference', value: prefs.feel_preference },
      { label: 'Finish Style', value: prefs.finish_style }
    ];
  }

  function getGoalsFields(data: any): ReviewField[] {
    const goals = data.goals_challenges || {};
    return [
      { label: 'Primary Goal', value: goals.primary_goal, critical: true },
      { label: 'Target Handicap', value: goals.target_handicap },
      { label: 'Biggest Frustration', value: goals.biggest_frustration, critical: true },
      { label: 'Purchase Timeline', value: goals.purchase_timeline },
      { label: 'Practice Frequency', value: goals.practice_frequency },
      { label: 'Lesson Frequency', value: goals.lesson_frequency }
    ];
  }

  const handleConfirm = () => {
    // Check mandatory fields before allowing submission
    if (!validationResult.isValid) {
      setShowValidationError(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Proceed with submission
    onConfirm();
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Mandatory Fields Status */}
      <div className={`border-l-4 p-4 mb-6 ${
        validationResult.isValid
          ? 'bg-green/10 border-green-ink'
          : 'bg-orange-50 border-orange-400'
      }`}>
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-sm font-medium">
              {validationResult.isValid ? '✅' : '⚠️'} Mandatory Fields Status
            </h3>
            <p className="mt-1 text-sm">
              {validationResult.filledCount} of {validationResult.filledCount + validationResult.missingCount} required fields completed
            </p>
          </div>
          {validationResult.isValid ? (
            <span className="text-green-ink font-semibold">Ready to Submit!</span>
          ) : (
            <span className="text-orange-600 font-semibold">
              {validationResult.missingCount} field{validationResult.missingCount !== 1 ? 's' : ''} missing
            </span>
          )}
        </div>
      </div>

      {/* Validation Error Banner (shown when user tries to submit without required fields) */}
      {showValidationError && !validationResult.isValid && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Cannot Submit: Missing Required Fields
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p className="mb-2">Please fill in the following required fields before submitting:</p>
                <ul className="list-disc list-inside space-y-1">
                  {validationResult.missingFields.map((field) => (
                    <li key={field}>
                      {MANDATORY_FIELD_LABELS[field] || field}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Warning Banner */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              ⚠️ Important: Review Your Data Carefully
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                <strong>Most fields cannot be changed after submission.</strong> Please review
                all information below, especially swing speeds, distances, and handicap.
                These are critical for accurate equipment recommendations.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Title */}
      <h1 className="text-3xl font-bold mb-6">Review Your Profile</h1>

      {/* Sections */}
      {sections.map((section, sectionIndex) => {
        const fields = section.fields(formData);
        const hasData = fields.some(f => f.value !== null && f.value !== undefined && f.value !== '');

        return (
          <div key={sectionIndex} className="bg-white rounded-lg shadow mb-4">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold">{sectionIndex + 1}. {section.name}</h2>
              <button
                onClick={() => onEdit(sectionIndex)}
                className="text-green-ink hover:text-green-ink font-medium flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit
              </button>
            </div>
            <div className="px-6 py-4">
              {!hasData ? (
                <p className="text-gray-500 italic">No data provided for this section</p>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {fields.map((field, fieldIndex) => {
                    if (field.value === null || field.value === undefined || field.value === '') {
                      return null;
                    }

                    return (
                      <div
                        key={fieldIndex}
                        className={`${field.highlight ? 'bg-yellow-50 p-2 rounded' : ''}`}
                      >
                        <dt className="text-sm font-medium text-gray-500">
                          {field.label}
                          {field.critical && <span className="text-red-500 ml-1">*</span>}
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900 font-semibold">
                          {field.value}
                        </dd>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Equipment Profile Radar */}
      {(() => {
        const radarValues = computeRadarFromFormData(formData);
        return (
          <div className="mb-6">
            <RadarProfilePanel
              headRadar={radarValues.head}
              shaftRadar={radarValues.shaft}
            />
          </div>
        );
      })()}

      {/* Confirmation Checkbox */}
      <div className="bg-gray-50 rounded-lg p-6 mb-6">
        <label className="flex items-start cursor-pointer">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="mt-1 h-5 w-5 text-green-ink rounded"
          />
          <span className="ml-3 text-gray-700">
            I have reviewed all the information above and confirm it is accurate. I understand
            that <strong>most fields cannot be changed after submission</strong> and that
            inaccurate data will result in poor equipment recommendations.
          </span>
        </label>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          onClick={onCancel}
          className="flex-1 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-100"
        >
          ← Go Back to Form
        </button>
        <button
          onClick={handleConfirm}
          disabled={!confirmed || !validationResult.isValid}
          className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors ${
            confirmed && validationResult.isValid
              ? 'bg-green-ink text-white hover:opacity-90 shadow-lg'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {!validationResult.isValid
            ? `Missing ${validationResult.missingCount} Required Field${validationResult.missingCount !== 1 ? 's' : ''}`
            : !confirmed
            ? 'Please Confirm Accuracy Above'
            : '🏌️ Get Your Fit →'}
        </button>
      </div>
    </div>
  );
};

export default ProfileReviewPage;
